import { fetchSsrStyles, waitForElement } from "../../core/common/ssr-utils.js";
import { loadModuleScriptsFromHtml } from "../../core/common/ssr-scripts.js";
import { getSsrBaseUrl } from "./coursesu-env.js";
import { getPosExtId } from "./utils.js";

const RECIPE_TAGS_ENDPOINT = "/v3/recipe-tags";
const RECIPE_TAG_STYLES_ENDPOINT = "/v3/styles/recipe-tag";

let ssrTagsByProductId = null;

let currentlyInjecting = false;
let newInjectionToProcess = false;

function isBasketPage() {
  return globalThis.location.href.includes("/panier");
}

/* ******************************************************* SSR PREFETCH ******************************************************** */

/**
 * Prefetch recipe-tags from cart product ext IDs.
 * Does not inject into the DOM; stores HTML in `ssrTagsByProductId`.
 */
export async function prefetchRecipeTags(headers) {
  if (!isBasketPage()) {
    return;
  }

  const storeId = getPosExtId();
  if (!storeId) {
    return;
  }

  const container = await waitForElement(".cart-content-left-wrapper", {
    maxAttempts: 20,
    intervalMs: 250,
  });
  if (!container) {
    console.warn("[CoursesU] .cart-content-left-wrapper not found for recipe tags");
    return;
  }

  const productIds = collectProductIds();
  if (productIds.length === 0) {
    ssrTagsByProductId = {};
    return;
  }

  try {
    fetchSsrStyles(RECIPE_TAG_STYLES_ENDPOINT, getSsrBaseUrl());

    const data = await fetchRecipeTagsMultiple(headers, productIds);
    await Promise.all(data.map(({ html }) => loadModuleScriptsFromHtml(html)));

    ssrTagsByProductId = Object.fromEntries(
      data.map(({ productId, html }) => [productId, html])
    );
  } catch (err) {
    console.warn("[CoursesU] recipe tags SSR prefetch failed", err);
  }
}

function fetchRecipeTagsMultiple(headers, productIds) {
  const baseUrl = getSsrBaseUrl();
  if (!baseUrl) {
    throw new Error("[Mealz] baseUrl not configured");
  }

  const search = new URLSearchParams({
    store_id: getPosExtId(),
    product_ids: JSON.stringify(productIds),
  });
  const url = `${baseUrl}${RECIPE_TAGS_ENDPOINT}?${search.toString()}`;

  return fetch(url, {
    method: "GET",
    headers,
  }).then(async (res) => {
    if (!res.ok) {
      throw new Error(`[Mealz] ${url} fetch failed: ${res.status} ${res.statusText}`);
    }

    const payload = await res.json();
    return payload?.data ?? [];
  });
}

function collectProductIds() {
  const cartRows = document.getElementsByClassName("cart-row");
  const ids = [...cartRows]
    .map((row) => row.dataset.itemid)
    .filter(Boolean);
  return [...new Set(ids)];
}

/* ******************************************************* TAG INJECTION ******************************************************** */

/**
 * Inject the recipe-tags on the basket page of CoursesU. The feature is old and we do not maintain it much,
 * but CoursesU wants us to maintain it
 */
export function initBasketTagInjection() {
  if (!isBasketPage()) {
    return;
  }

  const mutationTarget = document.getElementsByClassName("cart-content-left-wrapper")[0];
  if (!mutationTarget) {
    return;
  }

  const observedAtts = { attributes: false, childList: true, subtree: true };
  const basketObserver = new MutationObserver(() => {
    if (currentlyInjecting) {
      newInjectionToProcess = true;
    } else {
      injectRecipesTags();
    }
  });
  basketObserver.observe(mutationTarget, observedAtts);

  injectRecipesTags();
}

function injectRecipesTags() {
  newInjectionToProcess = false;
  currentlyInjecting = true;

  try {
    applyTagsFromCache();
  } catch (err) {
    console.warn("[CoursesU] recipe-tags injection failed", err);
  } finally {
    currentlyInjecting = false;
    if (newInjectionToProcess) {
      injectRecipesTags();
    }
  }
}

function applyTagsFromCache() {
  if (!ssrTagsByProductId) {
    return;
  }

  ensureTagContainers();

  const cartRows = document.getElementsByClassName("cart-row");
  [...cartRows].forEach((row) => {
    const extId = row.dataset.itemid;
    const container = row.querySelector(".mealz-recipe-tag-container");
    if (!container || !extId) {
      return;
    }
    if (container.querySelector("mealz-recipe-tag")) {
      return;
    }
    container.innerHTML = ssrTagsByProductId[extId] ?? "";
  });
}

function ensureTagContainers() {
  const cartRows = document.getElementsByClassName("cart-row");
  [...cartRows].forEach((row) => {
    if (row.querySelector(".mealz-recipe-tag-container")) {
      return;
    }
    const tagContainer = document.createRange().createContextualFragment(
      `<div class="mealz-recipe-tag-container"></div>`
    );
    row.appendChild(tagContainer);
  });
}
